import { useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { routesPaths, sortCallback, sortingAs } from '@selfserve/react-utils';

import { productSortingValues } from '../constants/product-sorting-values';
import { productStatusesTexts } from '../constants/status-texts';
import { AdminSearchInput } from './admin-search-input';
import { StyledTable, StyledTd, StyledTr } from '../ui/admin-styled-components';
import { TableHeaderButton } from '../ui/table-header-button';
import { useSortByUrl } from '../hooks/use-sort-by-url';

const StyledProductListContainer = styled('div')`
  margin-top: 24px;
`;

interface Props {
  products: any;
  tab: string;
}

const AdminProductList = ({ products, tab }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { productFamily } = useParams();
  const [search, setSearch] = useState('');
  const { sortValue, sortType } = useSortByUrl();

  const sortProducts = useCallback(
    (products: any) => {
      switch (sortValue) {
        default:
        case productSortingValues.NAME:
          return products?.sort(
            sortCallback('name', sortingAs.STRING, sortType)
          );
        case productSortingValues.LAST_MODIFIED:
          return products?.sort(
            sortCallback(['updated_at', 'created_at'], sortingAs.DATE, sortType)
          );
        case productSortingValues.TYPE:
          return products;
        case productSortingValues.STATUS:
          return products?.sort(
            sortCallback('status', sortingAs.NUMBER, sortType)
          );
      }
    },
    [sortValue, sortType]
  );

  const handleProductClick = useCallback(
    (name: string) => () => {
      navigate(
        routesPaths.ADMIN_PRODUCT_UPDATE.replace(
          ':productFamily',
          productFamily as string
        )
          .replace(':type', tab)
          .replace(':name', name)
      );
    },
    [tab, productFamily]
  );

  const finalizedList = useMemo(
    () =>
      sortProducts(
        products?.filter(({ name }: any) =>
          search
            ? name?.toLowerCase().includes(search?.trim()?.toLowerCase())
            : true
        )
      ),
    [products, search, sortProducts]
  );

  return (
    <StyledProductListContainer>
      <AdminSearchInput value={search} onChange={setSearch} />
      <StyledTable>
        <thead>
          <tr>
            <TableHeaderButton value={productSortingValues.NAME}>
              {t('common.name')}
            </TableHeaderButton>
            <TableHeaderButton value={productSortingValues.TYPE}>
              {t('common.type')}
            </TableHeaderButton>
            <TableHeaderButton value={productSortingValues.STATUS}>
              {t('common.status')}
            </TableHeaderButton>
            <TableHeaderButton value={productSortingValues.LAST_MODIFIED}>
              {t('common.last-modified')}
            </TableHeaderButton>
          </tr>
        </thead>
        <tbody>
          {finalizedList?.map((product: any) => {
            const { name, created_at, updated_at, status } = product;
            return (
              <StyledTr onClick={handleProductClick(name)}>
                <StyledTd>{name}</StyledTd>
                <StyledTd>-</StyledTd>
                <StyledTd>{productStatusesTexts[status]}</StyledTd>
                <StyledTd>
                  {moment(updated_at || created_at).format('MMM DD, YYYY')}
                </StyledTd>
              </StyledTr>
            );
          })}
        </tbody>
      </StyledTable>
    </StyledProductListContainer>
  );
};

export { AdminProductList };
